import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

// Prices longer than this ("$119.99" fits, "119,99 USD" doesn't) render a
// size smaller so they stay on one line in the plan cards on small phones.
const LONG_PRICE_LENGTH = 7

// Native only sends Apple's formatted displayPrice, so the yearly figure is
// read back out of that string and divided by 12 in place, keeping the
// storefront's symbol, spacing and separators:
// "$99.99" → "$8.33", "119,99 USD" → "10,00 USD", "¥12,000" → "¥1,000".
function perMonthPrice(displayPrice) {
  const raw = displayPrice.match(/\d(?:[\d.,\s\u00a0\u202f'\u2019]*\d)?/)?.[0]
  if (!raw) return null

  const fraction = raw.match(/[.,](\d{1,2})$/)
  const decimals = fraction ? fraction[1].length : 0
  const decimalSep = fraction ? raw[raw.length - decimals - 1] : ""
  const intPart = fraction ? raw.slice(0, -(decimals + 1)) : raw
  const groupSep = intPart.match(/\D/)?.[0] || ""
  const yearly = Number(`${intPart.replace(/\D/g, "")}.${fraction ? fraction[1] : "0"}`)
  if (!yearly) return null

  const [whole, cents] = (yearly / 12).toFixed(decimals).split(".")
  const grouped = groupSep ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep) : whole
  return displayPrice.replace(raw, cents ? `${grouped}${decimalSep}${cents}` : grouped)
}

// Bridges the in-app purchase paywall: HTML triggers a native StoreKit sheet,
// native replies with product metadata, purchase outcomes, and entitlement
// updates. The web side is the trigger and result-renderer; entitlement truth
// is server-state, refreshed by Turbo-visiting the page after purchase.
export default class extends BridgeComponent {
  static component = "subscription"
  static values = {
    productIds: Array,
    locale:     { type: String, default: "en" },
    selected:   { type: String, default: "" },
    monthlyId:  { type: String, default: "" },
    yearlyId:   { type: String, default: "" },
    appAccountToken: { type: String, default: "" },
    // The trial explainer mounts this controller purely to read live App Store
    // prices — it has no purchase button, so it must not count as a paywall view.
    trackView: { type: Boolean, default: true },
    successUrl:      { type: String, default: "" },
    startingMessage: { type: String, default: "" },
    errorMessage:    { type: String, default: "" },
    pendingMessage:  { type: String, default: "" },
    nothingRestoredMessage: { type: String, default: "" }
  }
  static targets = ["status", "monthlyPrice", "yearlyPrice", "yearlyPerMonth", "monthlyCard", "yearlyCard", "scroller", "sheet"]

  connect() {
    super.connect()
    this.#log(`connect — productIds=${JSON.stringify(this.productIdsValue)}`)
    if (this.trackViewValue) this.#track("paywall_viewed")
    this.#setupPeekReveal()
    if (this.productIdsValue.length === 0) return

    this.send(
      "loadProducts",
      { productIds: this.productIdsValue, locale: this.localeValue },
      (message) => this.#handleMessage(message)
    )
  }

  disconnect() {
    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll)
      document.removeEventListener("scroll", this._onScroll, true)
      if (this.hasScrollerTarget) {
        this.scrollerTarget.removeEventListener("scroll", this._onScroll)
      }
    }
    super.disconnect?.()
  }

  // Peek mascots stay hidden until the user scrolls the paywall body. Once
  // revealed they remain visible; the visible one is whichever mascot is
  // tagged with the currently selected plan. Scroll can happen on the window,
  // the scrollable inner div, or any ancestor — listen broadly and reveal on
  // the first non-trivial movement, then detach.
  #setupPeekReveal() {
    if (!this.hasSheetTarget) return
    const reveal = () => {
      this.sheetTarget.setAttribute("data-peeks-revealed", "")
      window.removeEventListener("scroll", this._onScroll)
      document.removeEventListener("scroll", this._onScroll, true)
      if (this.hasScrollerTarget) {
        this.scrollerTarget.removeEventListener("scroll", this._onScroll)
      }
    }
    this._onScroll = (event) => {
      const t = event?.target
      const top = (t && t.scrollTop != null ? t.scrollTop : null)
              ?? window.scrollY
              ?? document.documentElement.scrollTop
              ?? 0
      if (top > 8) reveal()
    }
    window.addEventListener("scroll", this._onScroll, { passive: true })
    document.addEventListener("scroll", this._onScroll, { capture: true, passive: true })
    if (this.hasScrollerTarget) {
      this.scrollerTarget.addEventListener("scroll", this._onScroll, { passive: true })
    }
  }

  // Fired when the user taps a plan card (yearly / monthly).
  select(event) {
    event.preventDefault()
    const id = event.params?.id
    if (!id) return
    this.selectedValue = id
    this.#paintSelection()
  }

  // Fired by the bottom Continue button — purchases the currently selected plan.
  purchaseSelected(event) {
    event?.preventDefault?.()
    const productId = this.selectedValue || this.yearlyIdValue || this.monthlyIdValue
    if (!productId) return
    this.#log(`purchaseSelected — id=${productId}`)
    this.#track("paywall_continue_clicked", { plan: this.#planFor(productId) })

    this.send("purchase", { productId, appAccountToken: this.appAccountTokenValue }, (message) => {
      this.#handleMessage(message)
    })
  }

  // Direct purchase from a single button (still supported).
  purchase(event) {
    event.preventDefault()
    const productId = event.params?.productId
    if (!productId) return
    this.send("purchase", { productId, appAccountToken: this.appAccountTokenValue }, (message) => this.#handleMessage(message))
  }

  restore(event) {
    event?.preventDefault?.()
    this.#log("restore tapped")

    // Immediate feedback. Native AppStore.sync() can take several seconds
    // (Apple ID prompt, network round-trip) — without an instant status
    // update the user thought the tap was being dropped on the floor.
    if (this.startingMessageValue) {
      this.#showStatus(this.startingMessageValue, { error: false })
    }

    this.send("restore", {}, (message) => {
      this.#handleMessage(message)
    })
  }

  selectedValueChanged() {
    this.#paintSelection()
  }

  #paintSelection() {
    const selected = this.selectedValue
    if (this.hasYearlyCardTarget) {
      this.#toggleCard(this.yearlyCardTarget, selected === this.yearlyIdValue)
    }
    if (this.hasMonthlyCardTarget) {
      this.#toggleCard(this.monthlyCardTarget, selected === this.monthlyIdValue)
    }
    const plan = selected === this.monthlyIdValue ? "monthly"
               : selected === this.yearlyIdValue  ? "yearly"
               : null
    if (!plan) return

    if (this.hasSheetTarget) {
      this.sheetTarget.setAttribute("data-selected-plan", plan)
    }
    // Segmented-control form of the same selection (trial explainer): the
    // root carries the plan so CSS can paint the active segment and reveal
    // the matching price line.
    this.element.setAttribute("data-selected-plan", plan)
    this.element.querySelectorAll("[data-plan-segment]").forEach((segment) => {
      segment.setAttribute("aria-pressed", String(segment.dataset.planSegment === plan))
    })
  }

  #toggleCard(card, on) {
    card.classList.toggle("paywall-plan-card-selected", on)
    const radio = card.querySelector(".paywall-radio")
    if (!radio) return
    radio.classList.toggle("paywall-radio-on", on)
    if (on && !radio.querySelector("svg")) {
      radio.innerHTML = `<svg class="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>`
    } else if (!on) {
      radio.innerHTML = ""
    }
  }

  #handleMessage(message) {
    const { event, data } = message || {}
    this.#log(`recv ${event}`)
    switch (event) {
      case "productsLoaded":
        this.#renderPrices(data?.products || [])
        break
      case "purchaseCompleted":
        this.#track("paywall_purchase_completed", { plan: this.#planFor(this.selectedValue) })
        this.#refreshAfterPurchase()
        break
      case "restored":
        this.#refreshAfterPurchase()
        break
      case "nothingRestored":
        // No entitlements found to restore. Keep the paywall open and show a
        // plain (non-error) message instead of silently navigating away.
        this.#showStatus(this.nothingRestoredMessageValue || "No purchases to restore.", { error: false })
        break
      case "purchaseCancelled":
        this.#track("paywall_purchase_cancelled", { plan: this.#planFor(this.selectedValue) })
        break
      case "purchasePending":
        // Ask-to-Buy / parental approval. Render as an informational
        // message, not an error — the transaction will arrive later.
        this.#showStatus(this.pendingMessageValue || "Waiting for approval.", { error: false })
        break
      case "purchaseFailed":
        this.#showStatus(data?.message || "Purchase failed", { error: true })
        break
    }
  }

  #showStatus(message, { error = true } = {}) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden")
    // Toggle the red error styling off for non-error states (e.g. pending).
    this.statusTarget.classList.toggle("text-error", error)
    this.statusTarget.classList.toggle("text-base-content/70", !error)
  }

  #renderPrices(products) {
    products.forEach((product) => {
      if (!product.displayPrice) return
      this.#priceTargetsFor(product.id).forEach((target) => {
        target.textContent = product.displayPrice
        target.toggleAttribute("data-price-long", product.displayPrice.length > LONG_PRICE_LENGTH)
      })
      if (product.id === this.yearlyIdValue) this.#renderYearlyPerMonth(product.displayPrice)
    })
  }

  #renderYearlyPerMonth(displayPrice) {
    const amount = perMonthPrice(displayPrice)
    if (!amount) return
    this.yearlyPerMonthTargets.forEach((target) => {
      target.textContent = target.dataset.template.replace("%{amount}", amount)
    })
  }

  // The trial explainer prints the selected plan's price twice — once in the
  // subtitle and once in the day-7 charge line — so every matching target is
  // filled, not just the first one in the DOM.
  #priceTargetsFor(productId) {
    if (productId === this.yearlyIdValue) return this.yearlyPriceTargets
    if (productId === this.monthlyIdValue) return this.monthlyPriceTargets
    return []
  }

  #refreshAfterPurchase() {
    // Drop Turbo's snapshot cache so any subsequent navigation refetches
    // with the freshly active subscription state instead of serving the
    // stale "Upgrade your subscription" banner from cache.
    try { window.Turbo?.cache?.clear?.() } catch (_) {}

    // When this paywall is the closing slide of onboarding, the onboarding
    // controller owns what happens next: POST `complete` to flip `onboarded`,
    // then navigate. It claims the transition by cancelling this event. On
    // /billing/upgrade nothing listens, so the existing behaviour below runs
    // untouched.
    if (this.dispatch("completed", { cancelable: true }).defaultPrevented) return

    // Inside Hotwire Native, the native SubscriptionComponent dismisses
    // the paywall modal and reloads the main session itself once the
    // purchase/restore is server-verified. Issuing a Turbo.visit from
    // inside the about-to-be-dismissed modal webview was routing the
    // destination into the modal stack on iOS, trapping the user on a
    // page whose nav-bar bridge attaches to a hidden system nav item.
    // Skip the visit when running inside Hotwire Native; let native drive.
    if (this.#isHotwireNative()) return

    // Web fallback (non-native browsers): navigate to the success URL.
    const target = this.successUrlValue || window.location.pathname + window.location.search
    if (window.Turbo) {
      window.Turbo.visit(target, { action: "replace" })
    } else {
      window.location.href = target
    }
  }

  #isHotwireNative() {
    // Hotwire Native injects the bridge runtime as `window.HotwireNative`
    // (and the user agent carries "Hotwire Native iOS"). Either signal
    // is sufficient to know the modal will be dismissed by the host.
    if (typeof window !== "undefined" && window.HotwireNative) return true
    const ua = (navigator?.userAgent || "")
    return ua.includes("Hotwire Native") || ua.includes("Turbo Native")
  }

  #log(msg) {
    // Surfaces in Safari Web Inspector when attached to the iOS simulator.
    console.log(`[bridge--subscription] ${msg}`)
  }

  // Map a StoreKit product id back to a human plan name for analytics props.
  #planFor(productId) {
    if (productId === this.yearlyIdValue) return "yearly"
    if (productId === this.monthlyIdValue) return "monthly"
    return "unknown"
  }

  // Fire a PostHog event. posthog-js is only present in production (see
  // shared/_posthog), so this is a no-op in dev. Wrapped so a missing or
  // half-initialised global can never break the paywall.
  #track(event, data = {}) {
    try {
      window.posthog?.capture(event, data)
    } catch (_) { /* analytics must never interrupt the purchase flow */ }
  }
}
